import omitDeep from 'omit-deep';

import { getDecodedId } from './ProductUtils';
import { getCurrency } from './PriceUtils';

import { useState, useEffect } from 'react';

/**
 * Get Warehouse Items
 *
 * Have to loop through line items and add up each items quantity
 * @param {Array} lineItems - page index user is currently on
 * @param {STRING} type - 'WAREHOUSE' or 'VENDOR'
 * @return {Number} number of items
 */
export var getCartItems = function getCartItems(lineItems, type) {
  var virtualVendors = ['Virtual'];
  var photograVendors = ['Photogra'];
  var cheyenneVendors = ['Cheyenne'];
  var skylineFurnitureVendors = ['Skyline Furniture'];
  var twoDayVendors = ['Radial', 'Dormify Doing Good', 'Sleepyhead'];
  var customVendors = ['Elegant Linen', '48 Hour Monogram'];
  return lineItems.filter(function (lineItem) {
    return type === 'PHOTOGRA' ? photograVendors.includes(lineItem.node.variant.product.vendor) : type === 'VIRTUAL' ? virtualVendors.includes(lineItem.node.variant.product.vendor) : type === 'CHEYENNE' ? cheyenneVendors.includes(lineItem.node.variant.product.vendor) : type === 'SKYLINE_FURNITURE' ? skylineFurnitureVendors.includes(lineItem.node.variant.product.vendor) : type === 'WAREHOUSE' ? twoDayVendors.includes(lineItem.node.variant.product.vendor) : type === 'CUSTOM' ? customVendors.includes(lineItem.node.variant.product.vendor) : !twoDayVendors.includes(lineItem.node.variant.product.vendor) && !photograVendors.includes(lineItem.node.variant.product.vendor) && !customVendors.includes(lineItem.node.variant.product.vendor) && !cheyenneVendors.includes(lineItem.node.variant.product.vendor) && !skylineFurnitureVendors.includes(lineItem.node.variant.product.vendor) && !virtualVendors.includes(lineItem.node.variant.product.vendor);
  });
};
/**
 * Get Item Count
 *
 * Have to loop through line items and add up each items quantity
 * @param {Array} lineItems - page index user is currently on
 * @return {Number} number of items
 */
export var getItemCount = function getItemCount(lineItems) {
  return lineItems.reduce(function (count, item) {
    return count + item.node.quantity;
  }, 0);
};

/**
 * Get Vendor Custom Attributes
 *
 * We must add the vendor custom attribute for disabling shipping
 * in the checkout flow
 * This function adds custom attributes array with a _vendor key
 *
 * KEYS WITH _ WILL NOT SHOW UP ON CHECKOUT LINE ITEMS
 *
 * @param {string} vendor- products vendor string
 * @return {Array} customAttributes to pass to addVariantToCart func
 */
export var getVendorCustomAttribute = function getVendorCustomAttribute(vendor) {
  return [{ key: '_vendor', value: vendor }];
};

/**
 * Add Line Items To Swym
 *
 * Must pass items in cart to swym on page load
 * purely a side effect function, nothing is returned
 *
 *  @param {Array} lineItems list of line items
 *  @return nothing
 */
export var addLineItemsToSwym = function addLineItemsToSwym(lineItems) {
  var swymLineItems = lineItems.map(function (item) {
    return { id: getDecodedId(item.variantId) };
  });
  (function () {
    function initSwymCart(delay) {
      if (window.swymCart && window.swymCart.token) {
        window.swymCart.items = swymLineItems;
        window.SwymHasCartItems = swymLineItems.length > 0;
      } else {
        setTimeout(initSwymCart, delay * 2);
      }
    }
    initSwymCart(50);
  })();
};

/**
 * Get Created At Value
 *
 * Helper function that loops through customAttributes
 * and returns the addedAt value
 *
 * @param {Array} customAttributes - customAttributes array
 * @return {String} addedAt Value
 */
var getAddedAtValue = function getAddedAtValue(customAttributes) {
  var addedAt = customAttributes.find(function (attr) {
    return attr.key === '_addedAt';
  });
  return addedAt ? addedAt.value : null;
};

/**
 * Sort Items By Created At
 *
 * Loop through lineItems and return
 * a sorted array by created at attribute
 *
 * @param {Array} lineItems - LineItems array
 * @return {Array} Sorted lineItems by addedAt values
 */
export var sortItemsByAddedAt = function sortItemsByAddedAt(lineItems) {
  return lineItems.sort(function (a, b) {
    return new Date(getAddedAtValue(b.node.customAttributes)) - new Date(getAddedAtValue(a.node.customAttributes));
  });
};

/**
 * Create Line Items Array
 *
 * Remove __typename from obj
 * Loop through and grab items needed for [CheckoutLineItemInput!]!
 *
 * @param {Object} lineItems - Items in the cart
 * @return {Object} [CheckoutLineItemInput!]!
 */
export var createLineItems = function createLineItems(lineItems) {
  lineItems = omitDeep(lineItems, ['__typename']);
  return lineItems.map(function (item) {
    return {
      customAttributes: item.node.customAttributes,
      quantity: item.node.quantity,
      variantId: item.node.variant.id
    };
  });
};

/**
 * Send New Product To Trackers
 *
 * Need a way to send new products added to cart to
 * different plugins for tracking. This function is
 * purely a side effect function and nothing is returned
 *
 * 1. Check if there are any new products
 * 2. If new Products, loop through and get its attributes
 *    a. productId
 *    b. variantId
 *    c. productTitle
 *    d. productPrice
 * 3. If dataLayer exists, send to dataLayer
 * 4. If zaius exists, send to zaius
 *
 * @param {Array} - Old Line Items
 * @param {Array} - New Line Items
 * @return nothing
 */
export var sendProductToTrackers = function sendProductToTrackers(oldLineItems, newLineItems) {
  var newProducts = newLineItems.filter(function (item) {
    return !oldLineItems.some(function (x) {
      return x.node.id == item.node.id;
    });
  });

  if (newProducts.length) {
    newProducts.map(function (product) {
      var productId = getDecodedId(product.node.variant.product.id);
      var variantId = getDecodedId(product.node.variant.id);
      var productTitle = product.node.title;
      var productPrice = getCurrency(product.node.variant.priceV2.currencyCode, product.node.variant.priceV2.amount);
      if (window.dataLayer && productId && productTitle && productPrice && variantId) {
        window.dataLayer.push({
          event: 'add_to_cart',
          product_title: productTitle,
          product_id: productId,
          product_price: productPrice,
          variant_id: variantId
        });
      }

      if (window.zaius && variantId) {
        window.zaius.event('product', {
          action: 'add_to_cart',
          product_id: variantId
        });
      }
    });
  }
};

/**
 * Get Item's Custom Attribute Text Field
 *
 * @param {Object} item
 * @return {String or Undefined}
 */
export var getCustomAttributeTextField = function getCustomAttributeTextField(item) {
  var customTextField = item.customAttributes.find(function (attribute) {
    return attribute.key === 'Text';
  });
  return customTextField && customTextField.value;
};

export var checkIfItemIsVirtualGiftCard = function checkIfItemIsVirtualGiftCard(item) {
  return item.customAttributes.find(function (attribute) {
    return attribute.key.includes('_vgc');
  });
};

/**
 * Check If Item is in Cart
 *
 * Filter line items and see if current item is in cart already
 *
 * If current item has custom text (monogram item) also check if
 * the cart item has the same custom text field as well as same variant id
 * else check only if variant ids match
 *
 * @param {Array} lineItems - LineItems array
 * @return {Array} Sorted lineItems by addedAt values
 */
export var checkIfItemIsInCart = function checkIfItemIsInCart(currentItem, lineItems) {
  var currentItemCustomText = getCustomAttributeTextField(currentItem);
  var currentItemIsVirtualGiftCard = checkIfItemIsVirtualGiftCard(currentItem);
  var itemInCart = lineItems.filter(function (cartItem) {
    // Add Virtual Gift Card as separate item
    if (currentItemIsVirtualGiftCard) {
      return false;
    } else if (currentItemCustomText) {
      var cartItemCustomText = getCustomAttributeTextField(cartItem);
      return cartItem.variantId === currentItem.variantId && cartItemCustomText === currentItemCustomText;
    } else {
      return cartItem.variantId === currentItem.variantId;
    }
  });
  return itemInCart && itemInCart[0];
};